import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { Close, NavigateBefore, NavigateNext } from '@mui/icons-material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { setShowCodeValidationOptionsModal } from '../../actions/validation'
import { CodeValidationOption } from '../../models/Validation'
import { SceneManager } from '../../visualization/sceneManager'
import { Matrix, Vector3 } from '@babylonjs/core'

const optionsMock: CodeValidationOption[] = [
  {
    id: '277b2e4c-3460-467a-9919-1ef1261a4865',
    title: 'Relocate the Building',
    description:
      'Adjust the placement of the building on the property to ensure that the side yard setback meets or exceeds the 5-foot requirement. This might involve shifting the structure further away from the property line to comply with zoning regulations.',
  },
  {
    id: 'a6764f83-efb8-4f2c-95a2-f993c31ee169',
    title: 'Apply for a Variance',
    description:
      'Submit a request for a variance to the local zoning board. A variance is an exception to the zoning code that allows for deviation from specific requirements. You\u2019ll need to demonstrate that adhering to the setback is impractical or causes undue hardship.',
  },
  {
    id: '31639bd3-57e8-4211-9d3d-6d2c4dec137d',
    title: 'Redesign the Site Layout',
    description:
      'Modify the overall site plan to incorporate the setback requirement. This could include redesigning landscaping elements, driveways, or other structures to maintain the necessary distance from the property line without relocating the main building.',
  },
]

const OptionsModal = () => {
  const dispatch = useDispatch<any>()

  const isShowOptionsModal = useSelector(
    (state: RootState) => state.validation.isShowOptionsModal,
  )
  const selectedCodeValidationResult = useSelector(
    (state: RootState) => state.validation.selectedCodeValidationRule,
  )

  const [currentOptionIndex, setCurrentOptionIndex] = useState(0)

  const toPreviousOptions = useCallback(() => {
    const previousOptionIndex = currentOptionIndex - 1
    if (previousOptionIndex < 0) {
      return
    }

    setCurrentOptionIndex(previousOptionIndex)
  }, [currentOptionIndex])

  const toNextOptions = useCallback(() => {
    const nextOptionIndex = currentOptionIndex + 1
    if (nextOptionIndex > optionsMock.length) {
      return
    }

    setCurrentOptionIndex(nextOptionIndex)
  }, [currentOptionIndex])

  const currentOptionChar = useMemo(() => {
    const charCodes: number[] = []
    let tmp = currentOptionIndex
    while (tmp > 25) {
      charCodes.push(65 + Math.floor(tmp / 26) - 1)
      tmp = tmp - Math.floor(tmp / 26) * 26
    }
    charCodes.push(65 + Math.floor(tmp))

    return String.fromCharCode(...charCodes)
  }, [currentOptionIndex])

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elementId = selectedCodeValidationResult?.elementsIds?.[0]
    if (!elementId) return

    const scene = SceneManager.getInstance().getScene()
    const engine = SceneManager.getInstance().getEngine()
    const camera = SceneManager.getInstance().getCamera()
    const mesh = scene?.getMeshById(elementId)
    if (!scene || !camera || !engine || !mesh) return

    const observer = scene.onBeforeRenderObservable.add(() => {
      if (!closeButtonRef.current || !popoverRef.current) {
        return
      }

      const boundingVectors = mesh.getHierarchyBoundingVectors()
      const center = Vector3.Lerp(boundingVectors.min, boundingVectors.max, 0.5)
      const projected = Vector3.Project(
        center,
        Matrix.Identity(),
        scene.getTransformMatrix(),
        camera.viewport.toGlobal(
          engine.getRenderWidth(true),
          engine.getRenderHeight(true),
        ),
      )

      const appbarHeight = 52
      const buttonWidth = closeButtonRef.current.offsetWidth
      const buttonHeight = closeButtonRef.current.offsetHeight
      closeButtonRef.current.style.left = `${projected.x - buttonWidth / 2}px`
      closeButtonRef.current.style.top = `${projected.y - buttonHeight / 2 + appbarHeight}px`

      const rect = popoverRef.current.getBoundingClientRect()
      const validationTabWidth = 875
      if (rect.left < validationTabWidth) {
        popoverRef.current.style.left = 'anchor(right)'
        popoverRef.current.style.right = ''
      }

      if (rect.left > validationTabWidth + rect.width + buttonWidth) {
        popoverRef.current.style.left = ''
        popoverRef.current.style.right = 'anchor(left)'
      }

      if (rect.bottom > window.innerHeight) {
        popoverRef.current.style.top = ''
        popoverRef.current.style.bottom = 'anchor(top)'
      }

      if (rect.bottom < window.innerHeight - rect.height) {
        popoverRef.current.style.top = 'anchor(top)'
        popoverRef.current.style.bottom = ''
      }
    })

    return () => {
      scene.onBeforeRenderObservable.remove(observer)
    }
  }, [selectedCodeValidationResult])

  const closeModal = useCallback(() => {
    dispatch(setShowCodeValidationOptionsModal(false))
  }, [dispatch])

  return (
    <>
      {isShowOptionsModal && (
        <>
          <IconButton
            ref={closeButtonRef}
            onClick={closeModal}
            sx={{
              padding: 0,
              position: 'fixed',
              anchorName: '--optionsModalCloseButton',
            }}
          >
            <Close />
          </IconButton>
          <Box
            ref={popoverRef}
            sx={{
              position: 'fixed',
              positionAnchor: '--optionsModalCloseButton',
            }}
          >
            <Box
              sx={{
                borderRadius: '4px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '8px',
                maxWidth: '250px',
                minHeight: '150px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#04002E',

                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: 'normal',
                    }}
                  >
                    Option {currentOptionChar}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#BBBCBC',

                      fontSize: '11px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                    }}
                  >
                    {currentOptionIndex + 1} of {optionsMock.length}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#04002E',

                      fontSize: '11px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '12px',
                      textAlign: 'left',
                    }}
                  >
                    {optionsMock[currentOptionIndex].title}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#04002E',

                      fontSize: '11px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '12px',
                      textAlign: 'left',
                    }}
                  >
                    {optionsMock[currentOptionIndex].description}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '4px',
                  }}
                >
                  <Button
                    variant='outlined'
                    disabled={currentOptionIndex === 0}
                    onClick={toPreviousOptions}
                    sx={{
                      width: '24px',
                      height: '24px',
                      minWidth: '0px',
                      padding: '4px',
                      '&.MuiButton-outlined': {
                        backgroundColor: '#FCFCFC',
                        border: '1px solid #E6E6E6',
                      },
                      '&.MuiButton-outlined:hover': {
                        backgroundColor: '#F0F0F0',
                      },
                      '& .MuiTouchRipple-root': {
                        color: '#E6E6E6',
                      },
                    }}
                  >
                    <NavigateBefore
                      sx={{
                        color: '#BBBCBC',
                      }}
                    />
                  </Button>

                  <Button
                    variant='outlined'
                    disabled={currentOptionIndex >= optionsMock.length - 1}
                    onClick={toNextOptions}
                    sx={{
                      width: '24px',
                      height: '24px',
                      minWidth: '0px',
                      padding: '4px',
                      '&.MuiButton-outlined': {
                        backgroundColor: '#FCFCFC',
                        border: '1px solid #E6E6E6',
                      },
                      '&.MuiButton-outlined:hover': {
                        backgroundColor: '#F0F0F0',
                      },
                      '& .MuiTouchRipple-root': {
                        color: '#E6E6E6',
                      },
                    }}
                  >
                    <NavigateNext
                      sx={{
                        color: '#BBBCBC',
                      }}
                    />
                  </Button>
                </Box>

                <Button
                  disableElevation
                  variant='contained'
                  sx={{
                    textTransform: 'none',
                    height: '24px',
                    '&.MuiButton-contained': {
                      backgroundColor: '#CDF765',
                      borderRadius: '2px',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: '#04002E',
                      fontSize: '10px',

                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '8px',
                    }}
                  >
                    Select Option
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  )
}

export default OptionsModal
